/**
 * オペレーターマスタ(operators)。仕様書 §4.1 + 2026-09-09 スタッフ項目表 + ③指示。
 * - 1人が複数のBB ID(品川用SN / 在宅用SR / 不用品用など)を持つ → bb_accounts[] に統合。
 *   出勤形態・報酬区分・報酬(時給/件数単価)は **BB ID単位**。人の出勤形態・報酬区分は先頭アカウントを写す
 * - 報酬の変更は「改訂を追加」(BB IDを指定)でのみ行い、そのBB IDの wage_history[] に必ず残す
 * - 交通費(往復)も改訂で変更し commute_history[] に残す
 * - 登録・編集は audit_logs に残す(登録日・更新ログの表示に使う)
 */
import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { nextOperatorCode } from '@core/lib/numbering';
import { writeAuditLog } from '@core/lib/auditLog';
import { isValidDateStr, todayStr } from '@core/lib/date';
import { isValidAmount } from '@core/lib/money';
import {
  commuteOn,
  wageOn,
  type BbAccount,
  type CommuteRevision,
  type ExtraInfo,
  type InvoiceType,
  type Operator,
  type OperatorStatus,
  type PayType,
  type WageRevision,
  type WorkStyle,
  type Yen,
} from '@core/lib/types';

function withId<T>(snap: QueryDocumentSnapshot): T {
  return { id: snap.id, ...(snap.data() as object) } as T;
}

export type OperatorInput = {
  name: string;
  /** 新規のBB IDは current_wage が初期報酬になる。既存のBB IDの報酬は revisions でのみ変わる */
  bb_accounts: BbAccount[];
  status: OperatorStatus;
  /** 新規登録時の初期交通費。編集時は commute_revisions でのみ変わる */
  commute_cost: Yen;
  joined_at: string | null;
  left_at: string | null;
  extra_info: ExtraInfo[];
  note: string;
  /** インボイス控除の区分(在宅のみ。計算には使わない)。2026-09-17 MTG */
  invoice_type: InvoiceType | null;
  /** PCレンタル あり/なし */
  pc_rental: boolean;
  /** 本人が成績表を閲覧できるか(スタッフページで参照) */
  stats_visible: boolean;
};

/** 「改訂を追加」の1件(BB IDを指定) */
export type WageRevisionInput = { bb_id: string; effective_from: string; wage: Yen; reason: string };
/** 交通費の改訂1件 */
export type CommuteRevisionInput = { effective_from: string; amount: Yen; reason: string };

function normalizeAccount(a: BbAccount): BbAccount {
  return {
    work_style: a.work_style,
    // タイミーは時給のみ
    pay_type: a.work_style === 'timee' ? 'hourly' : a.pay_type,
    bb_id: a.bb_id.trim(),
    ext: a.ext?.trim() || null,
    company_id: a.company_id || null,
    business: a.business,
    active: a.active,
    current_wage: a.current_wage,
    wage_history: a.wage_history ?? [],
  };
}

function cleanAccounts(list: BbAccount[]): BbAccount[] {
  return list.filter((a) => a.bb_id.trim() !== '').map(normalizeAccount);
}

function cleanExtra(list: ExtraInfo[]): ExtraInfo[] {
  return list
    .map((e) => ({ label: e.label.trim(), value: e.value.trim() }))
    .filter((e) => e.label !== '' || e.value !== '');
}

function validate(input: OperatorInput): void {
  if (!input.name.trim()) throw new Error('名前を入力してください');
  if (!isValidAmount(input.commute_cost)) throw new Error('交通費は0以上の整数(円)で入力してください');
  if (input.joined_at && !isValidDateStr(input.joined_at)) throw new Error('入社日の形式が正しくありません');
  if (input.left_at && !isValidDateStr(input.left_at)) throw new Error('退職日の形式が正しくありません');
  const accounts = cleanAccounts(input.bb_accounts);
  if (accounts.length === 0) throw new Error('BB IDを1つ以上登録してください(出勤形態を選ぶと自動生成されます)');
  for (const a of accounts) {
    if (!isValidAmount(a.current_wage)) throw new Error(`${a.bb_id} の時給／単価は0以上の整数(円)で入力してください`);
  }
  const ids = accounts.map((a) => a.bb_id.toLowerCase());
  if (new Set(ids).size !== ids.length) throw new Error('BB IDが重複しています');
}

function sortByDate<T extends { effective_from: string }>(history: T[]): T[] {
  return [...history].sort((a, b) => (a.effective_from < b.effective_from ? -1 : a.effective_from > b.effective_from ? 1 : 0));
}

function toWageRevisions(list: WageRevisionInput[], userId: string): WageRevision[] {
  return list.map((r) => {
    if (!r.bb_id) throw new Error('改訂するBB IDを選んでください');
    if (!isValidDateStr(r.effective_from)) throw new Error('改訂の適用開始日を選んでください');
    if (!isValidAmount(r.wage)) throw new Error('改訂後の金額は0以上の整数(円)で入力してください');
    if (!r.reason.trim()) throw new Error('改訂の理由を入力してください');
    return { wage: r.wage, effective_from: r.effective_from, reason: r.reason.trim(), changed_by: userId, changed_at: Timestamp.now() };
  });
}

function toCommuteRevisions(list: CommuteRevisionInput[], userId: string): CommuteRevision[] {
  return list.map((r) => {
    if (!isValidDateStr(r.effective_from)) throw new Error('交通費改訂の適用開始日を選んでください');
    if (!isValidAmount(r.amount)) throw new Error('改訂後の交通費は0以上の整数(円)で入力してください');
    if (!r.reason.trim()) throw new Error('交通費改訂の理由を入力してください');
    return { amount: r.amount, effective_from: r.effective_from, reason: r.reason.trim(), changed_by: userId, changed_at: Timestamp.now() };
  });
}

/**
 * BB IDごとの報酬履歴を組み立てる。
 * 既存のBB IDは履歴を引き継ぎ、新しいBB IDは入力の current_wage を初期登録として履歴の1件目にする。
 * 指定された改訂(bb_id一致)を追記し、今日時点の金額を current_wage に写す
 */
function buildAccounts(
  inputAccounts: BbAccount[],
  current: Operator | null,
  revisions: WageRevisionInput[],
  userId: string,
  initial: { date: string; reason: string },
): BbAccount[] {
  const revs = toWageRevisions(revisions, userId);
  const today = todayStr();
  return cleanAccounts(inputAccounts).map((a) => {
    const existing = current?.bb_accounts.find((c) => c.bb_id.toLowerCase() === a.bb_id.toLowerCase());
    const base: WageRevision[] = existing
      ? [...(existing.wage_history ?? [])]
      : [{ wage: a.current_wage, effective_from: initial.date, reason: initial.reason, changed_by: userId, changed_at: Timestamp.now() }];
    revisions.forEach((r, i) => {
      if (r.bb_id.toLowerCase() === a.bb_id.toLowerCase()) base.push(revs[i]!);
    });
    const history = sortByDate(base);
    const fallback = existing?.current_wage ?? a.current_wage;
    return { ...a, wage_history: history, current_wage: wageOn({ current_wage: fallback, wage_history: history }, today) };
  });
}

/** 人の出勤形態・報酬区分は先頭アカウントの値(一覧・フィルタ・金額欄の文言に使う) */
export function primaryStyle(accounts: BbAccount[]): { work_style: WorkStyle; pay_type: PayType } {
  const first = accounts[0];
  return { work_style: first?.work_style ?? 'shinagawa', pay_type: first?.pay_type ?? 'hourly' };
}

function toDocument(input: OperatorInput, accounts: BbAccount[]) {
  return {
    name: input.name.trim(),
    bb_accounts: accounts,
    ...primaryStyle(accounts),
    status: input.status,
    joined_at: input.joined_at || null,
    left_at: input.left_at || null,
    extra_info: cleanExtra(input.extra_info),
    note: input.note.trim() || null,
    // インボイス控除は在宅のBB IDがある人だけ持つ(在宅以外は区分を残さない)
    invoice_type: accounts.some((a) => a.work_style === 'zaitaku') ? (input.invoice_type ?? null) : null,
    pc_rental: input.pc_rental === true,
    stats_visible: input.stats_visible === true,
  };
}

export async function fetchOperators(): Promise<Operator[]> {
  const snap = await getDocs(query(collection(getDb(), 'operators'), orderBy('op_code')));
  return snap.docs.map((d) => withId<Operator>(d));
}

/** 1人だけ読む(operator は自分のドキュメントだけ読めるため、一覧は使えない) */
export async function fetchOperatorById(id: string): Promise<Operator | null> {
  const snap = await getDoc(doc(getDb(), 'operators', id));
  return snap.exists() ? ({ id: snap.id, ...(snap.data() as object) } as Operator) : null;
}

/** 登録。各BB IDの初期報酬と交通費も履歴の1件目として残す(理由=初期登録) */
export async function createOperator(
  input: OperatorInput,
  userId: string,
  options: { source?: 'manual' | 'import'; revisions?: WageRevisionInput[]; commuteRevisions?: CommuteRevisionInput[] } = {},
): Promise<string> {
  validate(input);
  const initial = { date: input.joined_at || todayStr(), reason: options.source === 'import' ? 'CSVインポート(初期登録)' : '初期登録' };
  const accounts = buildAccounts(input.bb_accounts, null, options.revisions ?? [], userId, initial);
  const commuteHistory = sortByDate([
    { amount: input.commute_cost, effective_from: initial.date, reason: initial.reason, changed_by: userId, changed_at: Timestamp.now() },
    ...toCommuteRevisions(options.commuteRevisions ?? [], userId),
  ]);
  const opCode = await nextOperatorCode();
  const ref = doc(collection(getDb(), 'operators'));
  const now = serverTimestamp();
  await setDoc(ref, {
    op_code: opCode,
    ...toDocument(input, accounts),
    commute_cost: commuteOn({ commute_cost: input.commute_cost, commute_history: commuteHistory }, todayStr()),
    commute_history: commuteHistory,
    uid: null,
    created_by: userId,
    created_at: now,
    updated_by: userId,
    updated_at: now,
  });
  await writeAuditLog({
    targetType: 'operator',
    targetId: ref.id,
    action: options.source === 'import' ? 'operator-import' : 'operator-create',
    by: userId,
    after: { op_code: opCode, name: input.name.trim(), wages: Object.fromEntries(accounts.map((a) => [a.bb_id, a.current_wage])) },
  });
  return ref.id;
}

/**
 * 更新。報酬は「改訂を追加」(revisions)でのみ、交通費は commuteRevisions でのみ変わる。
 * changes は画面側で作った「項目: 旧 → 新」の文章(変更履歴に残す)
 */
export async function updateOperator(params: {
  current: Operator;
  input: OperatorInput;
  revisions: WageRevisionInput[];
  commuteRevisions: CommuteRevisionInput[];
  changes: string[];
  userId: string;
}): Promise<void> {
  const { current, input, revisions, commuteRevisions, changes, userId } = params;
  validate({ ...input, commute_cost: current.commute_cost });
  const accounts = buildAccounts(input.bb_accounts, current, revisions, userId, { date: todayStr(), reason: 'BB ID追加(初期登録)' });
  const commuteHistory = sortByDate([...(current.commute_history ?? []), ...toCommuteRevisions(commuteRevisions, userId)]);
  const commute = commuteOn({ commute_cost: current.commute_cost, commute_history: commuteHistory }, todayStr());
  await updateDoc(doc(getDb(), 'operators', current.id), {
    ...toDocument(input, accounts),
    commute_cost: commute,
    commute_history: commuteHistory,
    updated_by: userId,
    updated_at: serverTimestamp(),
  });
  await writeAuditLog({
    targetType: 'operator',
    targetId: current.id,
    action: revisions.length > 0 || commuteRevisions.length > 0 ? 'operator-wage-change' : 'operator-update',
    by: userId,
    before: { wages: Object.fromEntries(current.bb_accounts.map((a) => [a.bb_id, a.current_wage])), commute: current.commute_cost },
    after: { wages: Object.fromEntries(accounts.map((a) => [a.bb_id, a.current_wage])), commute },
    changes,
  });
}

/** 削除(取込ミスの取り消し用)。通常は在籍を「退職」に変更する */
export async function deleteOperator(operator: Operator, userId: string): Promise<void> {
  await deleteDoc(doc(getDb(), 'operators', operator.id));
  await writeAuditLog({
    targetType: 'operator',
    targetId: operator.id,
    action: 'operator-delete',
    by: userId,
    before: { op_code: operator.op_code, name: operator.name },
  });
}

/** CSVインポートで既存オペレーターへBB IDを追記する(追記分の報酬は初期登録として履歴に残す) */
export async function appendBbAccounts(operator: Operator, accounts: BbAccount[], userId: string): Promise<number> {
  const existing = new Set(operator.bb_accounts.map((a) => a.bb_id.toLowerCase()));
  const added = buildAccounts(accounts, null, [], userId, { date: todayStr(), reason: 'CSVインポート(初期登録)' }).filter(
    (a) => !existing.has(a.bb_id.toLowerCase()),
  );
  if (added.length === 0) return 0;
  await updateDoc(doc(getDb(), 'operators', operator.id), {
    bb_accounts: [...operator.bb_accounts, ...added],
    updated_by: userId,
    updated_at: serverTimestamp(),
  });
  await writeAuditLog({
    targetType: 'operator',
    targetId: operator.id,
    action: 'operator-update',
    by: userId,
    changes: added.map((a) => `BB IDを追加: ${a.bb_id}(CSVインポート)`),
  });
  return added.length;
}

/** operator ロールのログイン(uid)との紐付け(ユーザー管理から使う) */
export async function linkOperatorUid(operatorId: string, uid: string | null): Promise<void> {
  await updateDoc(doc(getDb(), 'operators', operatorId), { uid, updated_at: serverTimestamp() });
}

/** import_logs への記録(オペレーター/会社CSV共通) */
export async function writeImportLog(entry: {
  type: 'operators' | 'companies';
  file_name: string;
  file_hash: string;
  row_count: number;
  imported: number;
  merged: number;
  skipped: number;
  error_count: number;
  by: string;
}): Promise<void> {
  await setDoc(doc(collection(getDb(), 'import_logs')), {
    ...entry,
    created_at: serverTimestamp(),
  });
}
